import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollText, RefreshCw, ExternalLink, CheckCircle2, X, Mail, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  privacy: 'bg-violet-100 text-violet-800',
  tax: 'bg-amber-100 text-amber-800',
  payments: 'bg-emerald-100 text-emerald-800',
  ach: 'bg-emerald-100 text-emerald-800',
  payroll: 'bg-blue-100 text-blue-800',
  'consumer-protection': 'bg-rose-100 text-rose-800',
  labor: 'bg-blue-100 text-blue-800',
  other: 'bg-slate-100 text-slate-700',
};

const scoreColor = (s) => {
  if (s >= 90) return 'bg-red-100 text-red-800 border-red-200';
  if (s >= 60) return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const RegulatoryWatchCard = ({ token }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [filter, setFilter] = useState('high'); // 'high' (≥60) | 'all' | 'reviewed' | 'dismissed'
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const params = useCallback(() => {
    if (filter === 'high') return '?min_score=60&status=new';
    if (filter === 'reviewed') return '?status=reviewed';
    if (filter === 'dismissed') return '?status=dismissed';
    return '?min_score=0';
  }, [filter]);

  const fetchItems = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/mybookie/admin/regwatch/items${params()}`, auth);
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      // Silent — could be 403 if not admin
    } finally {
      setLoading(false);
    }
  }, [token, params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const pollNow = async () => {
    setPolling(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/admin/regwatch/poll-now`, {}, auth);
      toast.success(`Poll complete: ${res.data.new} new items, ${res.data.high_relevance} high relevance`);
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Poll failed');
    } finally {
      setPolling(false);
    }
  };

  const sendDigest = async () => {
    setEmailing(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/admin/regwatch/digest-now`, {}, auth);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest send failed');
    } finally {
      setEmailing(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`${API}/api/mybookie/admin/regwatch/items/${id}/status`, { status }, auth);
      await fetchItems();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  return (
    <Card className="border-[#E6E8E6]" data-testid="regwatch-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#0A2B1D] flex items-center justify-center">
              <ScrollText className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Regulatory Watch</CardTitle>
              <p className="text-xs text-[#5C6661] mt-0.5">Daily poll of Federal Register, NACHA, IRS, FTC, EDPB · LLM-scored for MyBookie relevance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={pollNow} disabled={polling} className="rounded-sm" data-testid="regwatch-poll-btn">
              {polling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Poll now
            </Button>
            <Button variant="outline" size="sm" onClick={sendDigest} disabled={emailing} className="rounded-sm" data-testid="regwatch-digest-btn">
              {emailing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Send digest
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap text-xs">
          {[
            { key: 'high', label: `High relevance (${items.length && filter === 'high' ? items.length : ''}`.trim() + (filter === 'high' ? ')' : '+)') },
            { key: 'all', label: 'All items (30d)' },
            { key: 'reviewed', label: `Reviewed (${counts.reviewed || 0})` },
            { key: 'dismissed', label: `Dismissed (${counts.dismissed || 0})` },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-2.5 py-1 rounded-sm transition ${filter === f.key ? 'bg-[#0A2B1D] text-white' : 'bg-[#F6F5F2] text-[#5C6661] hover:bg-[#E6E8E6]'}`}
              data-testid={`regwatch-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <p className="text-sm text-[#5C6661] py-4">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[#5C6661]">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
            <p className="text-sm font-medium">No items to review</p>
            <p className="text-xs mt-1">
              {filter === 'high'
                ? "Nothing high-relevance right now. The system polls daily — try 'All items' to see lower-scored hits."
                : "Hit 'Poll now' to fetch the latest from federal and state sources."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(it => (
              <div key={it.id} className="p-4 border border-[#E6E8E6] rounded-sm hover:border-[#B4C9BE] transition" data-testid={`regwatch-item-${it.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-sm border flex flex-col items-center justify-center ${scoreColor(it.score)}`}>
                    <div className="text-lg font-bold leading-none">{it.score}</div>
                    <div className="text-[9px] uppercase tracking-wider mt-0.5">/100</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="secondary" className={`${CATEGORY_COLORS[it.category] || CATEGORY_COLORS.other} text-xs`}>
                        {it.category}
                      </Badge>
                      <span className="text-xs text-[#8A948F]">{it.source_label}</span>
                      {it.published && <span className="text-xs text-[#8A948F]">· {String(it.published).slice(0, 10)}</span>}
                    </div>
                    <a href={it.url} target="_blank" rel="noopener noreferrer" className="font-medium text-[#111614] hover:text-[#0A2B1D] inline-flex items-start gap-1.5 leading-snug">
                      {it.title} <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-60" />
                    </a>
                    {it.score_reason && (
                      <p className="text-xs text-[#5C6661] mt-1.5 italic">"{it.score_reason}"</p>
                    )}
                    {it.summary && (
                      <p className="text-xs text-[#5C6661] mt-2 line-clamp-2 leading-relaxed">{it.summary.slice(0, 240)}{it.summary.length > 240 ? '…' : ''}</p>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                      {it.status === 'new' ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => updateStatus(it.id, 'reviewed')} className="h-7 text-xs rounded-sm" data-testid={`regwatch-mark-reviewed-${it.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Mark reviewed
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => updateStatus(it.id, 'dismissed')} className="h-7 text-xs text-[#5C6661]" data-testid={`regwatch-dismiss-${it.id}`}>
                            <X className="w-3 h-3 mr-1" /> Dismiss
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-[#8A948F]">
                          {it.status === 'reviewed' ? '✓ Reviewed' : '— Dismissed'}
                          {it.reviewed_by ? ` by ${it.reviewed_by}` : ''}
                          {it.reviewed_at ? ` · ${new Date(it.reviewed_at).toLocaleDateString()}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            This is a triage layer — not legal advice. Material changes still need attorney review before
            you update policies, terms, or customer-facing docs.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export { RegulatoryWatchCard };
export default RegulatoryWatchCard;
